import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import App from './App';
import { AppConfigContext, fetchAppConfig, type AppConfig } from './lib/config';
import { initNativeShell } from './lib/nativeShell';
import { notifyOtaReady } from './lib/otaUpdate';
import './styles/globals.css';

// Auto-reload quando o Service Worker ativa uma nova versão. Combinado com
// `workbox.skipWaiting + clientsClaim` em `vite.config.ts`, isso elimina o
// "tenho que fechar o app pra atualizar" — novas versões pegam no próximo
// evento de controllerchange, que dispara logo após o SW novo tomar controle.
//
// Guard: se o user está em `/active/<id>` (sessão de recarga ativa), adia o
// reload até ele navegar pra outra rota — evita perder o WebSocket no meio
// de um ciclo de carga. O novo bundle vai carregar naturalmente quando ele
// sair da tela.
if ('serviceWorker' in navigator) {
  let pendingReload = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (window.location.pathname.startsWith('/active/')) {
      pendingReload = true;
    } else {
      window.location.reload();
    }
  });
  // Quando sair da página de recarga ativa, aplica reload pendente se houver.
  window.addEventListener('popstate', () => {
    if (pendingReload && !window.location.pathname.startsWith('/active/')) {
      window.location.reload();
    }
  });
}

// Boot: busca config pública do backend antes de montar o app.
// `google_client_id` antes vinha de VITE_GOOGLE_CLIENT_ID em build-time,
// agora é runtime — uma só fonte de verdade no bow.env do backend.
async function bootstrap() {
  // Confirma ao OTA updater (nativo) que o bundle bootou — ANTES de qualquer
  // await de rede, pra conexão lenta não disparar rollback falso. No-op na web.
  void notifyOtaReady();

  const config: AppConfig = await fetchAppConfig();
  const clientId = config.google_client_id ?? '';

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <AppConfigContext.Provider value={config}>
        <GoogleOAuthProvider clientId={clientId}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </GoogleOAuthProvider>
      </AppConfigContext.Provider>
    </React.StrictMode>
  );

  // Shell nativo (status bar + esconder splash). No-op no PWA/web.
  void initNativeShell();
}

void bootstrap();
